<template>
    <div ref="partnershipSideButton">
        <NuxtLink :to="localePath('/partnership')" :class="`${locale} ${[dashboardPosition.includes('left') ? 'left-mode' : 'right-mode']}`" class="btn-container partnership" :aria-label="$t('partnership')">
            <div class="bg-main partnership-icon-bg text-content partnership-icon partnership-icon-container">
                <Icon iconClass="icon-partnership"></Icon>
            </div>
            <div class="partnership-text-container" :style="`height: ${contentContainerWidth}px`">
                <div ref="contentContainer" class="partnership-text">{{ $t('partnership') }}</div>
            </div>
        </NuxtLink>
    </div>
</template>

<script setup>
const localePath = useLocalePath()
const { locale } = useI18n();
const config = useRuntimeConfig();

const dashboardStore = useDashboardStore();
const mainStore = useMainStore();
const { isMobile, isIos, isAndroid } = useDevice();

const contentContainer = ref();
const contentContainerWidth = ref(0);
const getContentContainer = computed(() => {
    if (contentContainer.value) {
        return true
    }
    return false
})

watch(() => getContentContainer.value, (isTrue) => {
    if (isTrue) {
        contentContainerWidth.value = contentContainer.value.clientWidth;
    }
}, {
    immediate: true
})

const partnershipSideButton = ref(null);
const dashboardPosition = ref('bottom_left');


const currency = computed(() => {
    if (dashboardStore.ready && dashboardStore.data && dashboardStore.data.currency) {
        return dashboardStore.data.currency
    }
    return ''
});
const displayStatus = ref(false);

let resizeObserver = null

watch(partnershipSideButton, (el) => {
    if (el) {
        // Call it once on mount
        nextTick(() => setPosition())

        // Initialize ResizeObserver
        resizeObserver = new ResizeObserver(() => {
        setPosition()
        })
        resizeObserver.observe(el)
    }
    }, { immediate: true })

onBeforeUnmount(() => {
  if (resizeObserver) {
    resizeObserver.disconnect()
    resizeObserver = null
  }
})

const checkDisplayStatus = () => {
    // FPT-4598
    if(['production','staging'].includes(config.public.ENV)) {
        dashboardPosition.value = 'bottom_left';
        displayStatus.value = true;
        return
    }

    if(dashboardStore?.ready && mainStore?.generalData?.data?.[dashboardStore.data.currency]){
        dashboardPosition.value = mainStore.generalData?.data?.[dashboardStore.data.currency][0].general_display_setting[isMobile ? 'mobile': 'web'].find(x => x.type === 'partnership').display_position
        displayStatus.value = mainStore.generalData?.data?.[dashboardStore.data.currency][0].general_display_setting[isMobile ? 'mobile': 'web'].find(x => x.type === 'partnership').display_status
    }
}

watchEffect(() => {
    if(dashboardStore.ready) {
        checkDisplayStatus();
    }
})
watch(() => currency.value, () => {
    if(currency.value) {
        checkDisplayStatus();
    }
})

const setPosition = async () => {
    const box = document.querySelector('.partnership')
    const topBar = document.querySelector('.merchant-top-bar')
    
    const topBarRect = topBar?.getBoundingClientRect()
    if(!box) {
        return
    }

    switch (dashboardPosition.value) {
        case 'top_right':
            box.classList.remove('left-mode')
            box.classList.add('right-mode')
            box.classList.add('top_right')
            box.style.top = `${topBarRect?.height + 100}px`
        break
        case 'middle_right':
            box.classList.remove('left-mode')
            box.classList.add('right-mode')
            box.classList.add('middle_right')
            box.style.top = `${(window.innerHeight / 2) - (topBarRect?.height) + 100}px`
        break
        case 'bottom_right':
            box.classList.remove('left-mode')
            box.classList.add('right-mode')
            box.classList.add('bottom_right')
            box.style.bottom = `${50}px`
        break
        case 'top_left':
            box.classList.remove('right-mode')
            box.classList.add('left-mode')
            box.classList.add('top_left')
            box.style.top = `${topBarRect?.height + 100}px`
        break
        case 'middle_left':
            box.classList.remove('right-mode')
            box.classList.add('left-mode')
            box.classList.add('middle_left')
            box.style.top = `${(window.innerHeight / 2) - (topBarRect?.height) + 100}px`
        break
        case 'bottom_left':
            box.classList.remove('right-mode')
            box.classList.add('left-mode')
            box.classList.add('bottom_left')
            box.style.bottom = `${50}px`
        break
        default:
        break
    }
}

</script>

<style scoped>
.btn-container {
    width: 32px;
    position: fixed;
    color: var(--text-base);
    transform: translateY(-50%);
    box-shadow: 0px 0px 7px var(--primary);
    border: 1px solid var(--primary);
    background: var(--body);
    transition: all 0.3s;
    z-index: 10;
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;
    padding: 8px 0px;
    gap: 8px;
}
.btn-container.right-mode {
  right: 0px;
  transition: all 0.5s ease-out;
  border-top-left-radius: 10px;
  border-bottom-left-radius: 10px;
}
.btn-container.left-mode {
    left: 0px;
    transition: all 0.5s ease-out;
    border-top-right-radius: 10px;
    border-bottom-right-radius: 10px;
}

@media screen and (max-width:768px) {
    .btn-container {
    }
}

.btn-container.ne-np, 
.btn-container.th-th, 
.btn-container.km-kh, 
.btn-container.th-kh,
.btn-container.hi-in {
    min-height: 110px !important;
}

.btn-container.tl-ph {
    min-height: 150px !important;
}

.btn-container.id-id, .btn-container.ur-pk {
    min-height: 120px !important;
}

.btn-container.ms-my, .btn-container.bn-bd {
    min-height: 130px !important;
}

.partnership-icon-container {
    width: 22px;
    height: 22px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 100%;
    margin: 0 auto;
}

.partnership-icon-container i {
    font-size: 14px;
}

.partnership-text-container {
    width: 100%;
    position: relative;
    margin: 0 auto;
    display: flex;
    justify-content: center;
    align-items: center;
}

.partnership-text {
    font-weight: 400;
    font-size: 14px;
    white-space: nowrap;
    position: relative;
    transform: rotate(90deg);
    display: inline-block;
}
</style>